import type { HranyState } from "@/components/dielec/HranyEditor";
import type { Dielec, DielecUpdate } from "@/api/schemas";

/**
 * Preklad medzi dielcom a stavom hrán — **jediné miesto** v celom frontende,
 * ktoré vie, ako sa 4 hrany mapujú na 8 polí `hrana_x*_id` / `hrana_x*_auto`.
 *
 * Vytknuté v KOLE 67, keď k plnému detailu (`DielecDetailPanel`) pribudla
 * mini-lišta hrán (`HranyLista`). Dve kópie tohto mapovania by sa rozišli —
 * ten istý dôvod ako `lib/autoEdge.ts` (KOLO 58, preklad mm → hrana) alebo
 * `pricing.calculator.sucet_dielcov_zostavy` (KOLO 62b). Round-trip stráži
 * `lib/hrany.test.ts`.
 */

/** Hrany dielca zo servera → stav editora. Chýbajúca hrana = `auto`. */
export function toHrany(d: Dielec): HranyState {
  return {
    x1: { id: d.hrana_x1_id ?? null, auto: d.hrana_x1_auto ?? true },
    x2: { id: d.hrana_x2_id ?? null, auto: d.hrana_x2_auto ?? true },
    y1: { id: d.hrana_y1_id ?? null, auto: d.hrana_y1_auto ?? true },
    y2: { id: d.hrana_y2_id ?? null, auto: d.hrana_y2_auto ?? true },
  };
}

/** Polia hrán pre PATCH. Nič iné — volajúci si k tomu pridá, čo mení. */
export function hranyDoUpdate(hrany: HranyState): Partial<DielecUpdate> {
  return {
    hrana_x1_id: hrany.x1.id,
    hrana_x1_auto: hrany.x1.auto,
    hrana_x2_id: hrany.x2.id,
    hrana_x2_auto: hrany.x2.auto,
    hrana_y1_id: hrany.y1.id,
    hrana_y1_auto: hrany.y1.auto,
    hrana_y2_id: hrany.y2.id,
    hrana_y2_auto: hrany.y2.auto,
  };
}

/** Zmenila sa aspoň jedna hrana proti dielcu zo servera? */
export function hranySaZmenili(orig: Dielec, hrany: HranyState): boolean {
  const orig_hrany = toHrany(orig);
  return (["x1", "x2", "y1", "y2"] as const).some(
    (k) =>
      hrany[k].id !== orig_hrany[k].id || hrany[k].auto !== orig_hrany[k].auto,
  );
}

/**
 * Zaoblené strany masívu (KOLO 168) — masív sa neolepuje, strana sa
 * namiesto hrany zaoblí na agregáte olepovačky. Per strana ako hrana.
 * Či je dielec masív, rozhoduje backend (`dielec.masiv`).
 */
export interface ZaoblenieState {
  x1: boolean;
  x2: boolean;
  y1: boolean;
  y2: boolean;
}

/** Zaoblenie dielca zo servera → stav. Chýbajúce pole = bez zaoblenia. */
export function toZaoblenie(d: Dielec): ZaoblenieState {
  return {
    x1: d.zaoblenie_x1 ?? false,
    x2: d.zaoblenie_x2 ?? false,
    y1: d.zaoblenie_y1 ?? false,
    y2: d.zaoblenie_y2 ?? false,
  };
}

/** Polia zaoblenia pre PATCH — nič iné (lišta ich ukladá samé). */
export function zaoblenieDoUpdate(z: ZaoblenieState): Partial<DielecUpdate> {
  return {
    zaoblenie_x1: z.x1,
    zaoblenie_x2: z.x2,
    zaoblenie_y1: z.y1,
    zaoblenie_y2: z.y2,
  };
}

/** Zmenilo sa zaoblenie aspoň jednej strany proti dielcu zo servera? */
export function zaoblenieSaZmenilo(orig: Dielec, z: ZaoblenieState): boolean {
  const povodne = toZaoblenie(orig);
  return (["x1", "x2", "y1", "y2"] as const).some((k) => z[k] !== povodne[k]);
}
